import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Edit, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { apiRequest } from "@/lib/queryClient";
import { FinancialAssetFormModal } from "./financial-asset-form-modal";

// ============================================================================
// Types
// ============================================================================

type FinancialAssetType =
  | "inventory"
  | "debtors"
  | "term_deposit"
  | "shares_listed"
  | "shares_unlisted"
  | "managed_funds"
  | "bonds"
  | "other_financial";

interface FinancialAsset {
  id: string;
  securityId: string;
  financialAssetType: FinancialAssetType;
  description: string | null;
  // Inventory
  inventoryType: string | null;
  inventoryLocation: string | null;
  // Debtors
  debtorAging30: string | null;
  debtorAging60: string | null;
  debtorAging90Plus: string | null;
  concentrationTopDebtorPercent: string | null;
  // Institution / shares / funds / bonds / term deposit
  institutionName: string | null;
  accountNumber: string | null;
  numberOfUnits: string | null;
  unitPrice: string | null;
  asxCode: string | null;
  // Term deposit / bonds
  maturityDate: string | null;
  interestRate: string | null;
}

interface FinancialAssetDetailTabProps {
  securityId: string;
}

// ============================================================================
// Label maps
// ============================================================================

const ASSET_TYPE_LABELS: Record<FinancialAssetType, string> = {
  inventory: "Inventory",
  debtors: "Debtors",
  term_deposit: "Term Deposit",
  shares_listed: "Shares (Listed)",
  shares_unlisted: "Shares (Unlisted)",
  managed_funds: "Managed Funds",
  bonds: "Bonds",
  other_financial: "Other Financial Asset",
};

// ============================================================================
// Formatters
// ============================================================================

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatPercent(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return `${num.toFixed(2)}%`;
}

function calculateTotal(units: string | null, price: string | null): string | null {
  const u = parseFloat(units || "0");
  const p = parseFloat(price || "0");
  if (isNaN(u) || isNaN(p)) return null;
  const total = u * p;
  return formatCurrency(total.toString());
}

// ============================================================================
// Section renderers
// ============================================================================

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-sm text-muted-foreground">{label}</span>
      <p className="text-sm font-medium">{value}</p>
    </div>
  );
}

function renderInventorySection(asset: FinancialAsset) {
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Inventory Details
      </h4>
      <div className="grid grid-cols-2 gap-4">
        <InfoRow label="Inventory Type" value={asset.inventoryType || "-"} />
        <InfoRow label="Location" value={asset.inventoryLocation || "-"} />
        {asset.description && (
          <div className="col-span-2">
            <InfoRow label="Description" value={asset.description} />
          </div>
        )}
      </div>
    </div>
  );
}

function renderDebtorsSection(asset: FinancialAsset) {
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Debtor Aging
      </h4>
      <div className="grid grid-cols-2 gap-4">
        <InfoRow label="0–30 Days" value={formatCurrency(asset.debtorAging30)} />
        <InfoRow label="31–60 Days" value={formatCurrency(asset.debtorAging60)} />
        <InfoRow label="61–90+ Days" value={formatCurrency(asset.debtorAging90Plus)} />
        <InfoRow
          label="Top Debtor Concentration"
          value={formatPercent(asset.concentrationTopDebtorPercent)}
        />
      </div>
    </div>
  );
}

function renderSharesSection(asset: FinancialAsset, showAsxCode: boolean) {
  const calculatedTotal = calculateTotal(asset.numberOfUnits, asset.unitPrice);
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Holdings
      </h4>
      <div className="grid grid-cols-2 gap-4">
        {asset.institutionName && (
          <InfoRow label="Institution" value={asset.institutionName} />
        )}
        {showAsxCode && asset.asxCode && (
          <InfoRow label="ASX Code" value={asset.asxCode} />
        )}
        <InfoRow
          label="Number of Units"
          value={asset.numberOfUnits ? parseFloat(asset.numberOfUnits).toLocaleString("en-AU") : "-"}
        />
        <InfoRow label="Unit Price" value={formatCurrency(asset.unitPrice)} />
        <InfoRow label="Total Value" value={calculatedTotal || "-"} />
      </div>
    </div>
  );
}

function renderManagedFundsSection(asset: FinancialAsset) {
  const calculatedTotal = calculateTotal(asset.numberOfUnits, asset.unitPrice);
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Fund Holdings
      </h4>
      <div className="grid grid-cols-2 gap-4">
        {asset.institutionName && (
          <InfoRow label="Institution" value={asset.institutionName} />
        )}
        {asset.accountNumber && (
          <InfoRow label="Account Number" value={asset.accountNumber} />
        )}
        <InfoRow
          label="Number of Units"
          value={asset.numberOfUnits ? parseFloat(asset.numberOfUnits).toLocaleString("en-AU") : "-"}
        />
        <InfoRow label="Unit Price" value={formatCurrency(asset.unitPrice)} />
        <InfoRow label="Total Value" value={calculatedTotal || "-"} />
      </div>
    </div>
  );
}

function renderTermDepositSection(asset: FinancialAsset) {
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Deposit Details
      </h4>
      <div className="grid grid-cols-2 gap-4">
        {asset.institutionName && (
          <InfoRow label="Institution" value={asset.institutionName} />
        )}
        {asset.accountNumber && (
          <InfoRow label="Account Number" value={asset.accountNumber} />
        )}
        <InfoRow label="Maturity Date" value={formatDate(asset.maturityDate)} />
        <InfoRow label="Interest Rate" value={formatPercent(asset.interestRate)} />
      </div>
    </div>
  );
}

function renderBondsSection(asset: FinancialAsset) {
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Bond Details
      </h4>
      <div className="grid grid-cols-2 gap-4">
        {asset.institutionName && (
          <InfoRow label="Issuer / Institution" value={asset.institutionName} />
        )}
        {asset.accountNumber && (
          <InfoRow label="Account / Reference" value={asset.accountNumber} />
        )}
        <InfoRow label="Maturity Date" value={formatDate(asset.maturityDate)} />
        <InfoRow label="Interest Rate" value={formatPercent(asset.interestRate)} />
      </div>
    </div>
  );
}

function renderOtherSection(asset: FinancialAsset) {
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Details
      </h4>
      <div className="grid grid-cols-1 gap-4">
        <InfoRow label="Description" value={asset.description || "-"} />
      </div>
    </div>
  );
}

function renderAssetSection(asset: FinancialAsset) {
  switch (asset.financialAssetType) {
    case "inventory":
      return renderInventorySection(asset);
    case "debtors":
      return renderDebtorsSection(asset);
    case "shares_listed":
      return renderSharesSection(asset, true);
    case "shares_unlisted":
      return renderSharesSection(asset, false);
    case "managed_funds":
      return renderManagedFundsSection(asset);
    case "term_deposit":
      return renderTermDepositSection(asset);
    case "bonds":
      return renderBondsSection(asset);
    case "other_financial":
      return renderOtherSection(asset);
    default:
      return (
        <p className="text-sm text-muted-foreground">
          Unknown financial asset type.
        </p>
      );
  }
}

// ============================================================================
// Main component
// ============================================================================

export function FinancialAssetDetailTab({ securityId }: FinancialAssetDetailTabProps) {
  const [formModalOpen, setFormModalOpen] = useState(false);

  const { data: assetData, isLoading } = useQuery<FinancialAsset | null>({
    queryKey: ["/api/crm/securities", securityId, "financial-asset"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        `/api/crm/securities/${securityId}/financial-asset`
      );
      if (response.status === 404) {
        return null;
      }
      return response.json();
    },
    enabled: !!securityId,
  });

  const handleModalClose = (open: boolean) => {
    setFormModalOpen(open);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state — no financial asset recorded yet
  if (!assetData) {
    return (
      <>
        <Card data-testid="financial-asset-detail-tab">
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                No financial asset details recorded yet.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setFormModalOpen(true)}
                data-testid="button-add-financial-asset"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Financial Asset
              </Button>
            </div>
          </CardContent>
        </Card>

        <FinancialAssetFormModal
          open={formModalOpen}
          onOpenChange={handleModalClose}
          securityId={securityId}
          existingAsset={null}
        />
      </>
    );
  }

  // Detail view — asset exists
  const asset = assetData;
  const typeLabel = ASSET_TYPE_LABELS[asset.financialAssetType] ?? asset.financialAssetType;

  return (
    <>
      <Card data-testid="financial-asset-detail-tab">
        <CardContent className="pt-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Financial Asset Details</h3>
              <p className="text-sm text-muted-foreground mt-0.5">{typeLabel}</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setFormModalOpen(true)}
              data-testid="button-edit-financial-asset"
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </div>

          <div className="space-y-6">
            {renderAssetSection(asset)}
          </div>
        </CardContent>
      </Card>

      <FinancialAssetFormModal
        open={formModalOpen}
        onOpenChange={handleModalClose}
        securityId={securityId}
        existingAsset={asset}
      />
    </>
  );
}
